import { ListItemText } from '@mui/material';
import { useCallback, useState } from 'react';
import { REPORT_PDF } from 'src/api';
import axios from 'src/axios';
import { Button } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { handleErrorToast } from 'src/components/molecules';
import { ConfirmDialog } from 'src/components/organismsLegacy/ConfirmDialog';
import {
  CancelButton,
  ConfirmButton,
} from 'src/components/organismsLegacy/ConfirmDialog/ConfirmDialog.styles';
import { getReadableIdWithDash } from 'src/types';
import { isError } from 'src/utils';
import { downloadFile } from 'src/utils/commonFunctions';

type PDFState = 'idle' | 'downloading' | 'success' | 'error';

export interface DownloadReportButtonProps {
  reportId: string;
  isPartOfMenu?: boolean;
}

export const DownloadReportButton = ({
  reportId,
  isPartOfMenu = false,
}: DownloadReportButtonProps) => {
  const [pdfState, setPdfState] = useState<PDFState>('idle');
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const handleDownloadPdfClick = () => {
    setPdfState('idle');
    setShowConfirmDialog(true);
  };

  const handleReportDownload = useCallback(async () => {
    setPdfState('downloading');
    try {
      const response = await axios.get(REPORT_PDF(reportId), {
        timeout: 30000,
        responseType: 'blob',
      });
      downloadFile(
        new Blob([response, response.data]),
        `${getReadableIdWithDash(reportId)}.pdf`,
      );
      setPdfState('idle');
      setShowConfirmDialog(false);
    } catch (err) {
      if (
        isError(err) &&
        err.message === 'Request failed with status code 403'
      ) {
        handleErrorToast({ toastMessage: 'Could not retrieve the report' });
        setShowConfirmDialog(false);
        setPdfState('idle');
      } else {
        setPdfState('error');
      }
    }
  }, [reportId]);

  return (
    <>
      {!isPartOfMenu ? (
        <Button
          variant="tertiary"
          size="small"
          onClick={handleDownloadPdfClick}
          data-testid="download-report-button"
        >
          <>
            <StyledIconInButton
              size="small"
              position="left"
              className="uil uil-file-download"
            />
            Download report
          </>
        </Button>
      ) : (
        <ListItemText
          onClick={handleDownloadPdfClick}
          data-testid="download-report-button"
        >
          Download
        </ListItemText>
      )}
      <ConfirmDialog
        open={showConfirmDialog}
        setOpen={() => {}}
        title="Download Report?"
        // eslint-disable-next-line max-len
        body={
          pdfState === 'error'
            ? 'There was an error generating your PDF. Please try again or get in touch if the error persists.'
            : `Please note that once you download this information you will be responsible to secure this data and
        ensure that adequate data protection measures are taken as per your company compliance procedures.`
        }
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleReportDownload}
        data-testid="confirm-report-download-modal"
        confirmButton={
          <ConfirmButton
            onClick={handleReportDownload}
            data-testid="confirm-report-download"
            disabled={pdfState === 'downloading'}
          >
            {pdfState === 'error'
              ? 'Try again'
              : pdfState === 'downloading'
                ? 'Preparing document...'
                : 'Download'}
          </ConfirmButton>
        }
        cancelButton={
          <CancelButton
            onClick={() => setShowConfirmDialog(false)}
            data-testid="cancel-report-download"
          >
            CANCEL
          </CancelButton>
        }
      />
    </>
  );
};
